import React from 'react';
import { Checkbox, FormControl, ListItemText, MenuItem, Select } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { ApiService } from '../hooks';
import { extractApiErrorMessage, notifications } from '../utils';
import {
  ObservationCategoryOption,
  geneticFailureLabel,
  geneticFailureOptionsFrom,
  observationCategoryLabel
} from '../utils/animalObservations';

export interface AnimalObservationFilterValue {
  categories: string[];
  geneticFailures: string[];
}

export const EMPTY_ANIMAL_OBSERVATION_FILTER: AnimalObservationFilterValue = {
  categories: [],
  geneticFailures: []
};

interface IProps {
  disabled?: boolean;
  value: AnimalObservationFilterValue;
  onChange: (value: AnimalObservationFilterValue) => void;
  className?: string;
}

// Categories and genetic failures share one dropdown, so their ids are namespaced: nothing stops
// the backend from one day naming a defect the same as a category.
const CATEGORY_PREFIX = 'category:';
const FAILURE_PREFIX = 'failure:';

/**
 * The "Genetic failures and bites" dropdown (AB#3724). Multi-select, with the specific defects
 * nested under the genetic failure category.
 *
 * The options are served by the backend rather than hard-coded here, so adding a defect is one
 * backend deploy instead of a coordinated release of web and mobile. Each option now arrives with
 * its own translated label too, which is the only place the newer defect codes are worded - this
 * client has a translation key for `Other` alone. A label the backend does not send falls back to
 * the client's own key, and that in turn falls back to the raw id, so an option is always readable.
 */
const AnimalObservationFilter = (props: IProps) => {
  const { disabled, value, onChange, className } = props;
  const { t } = useTranslation();

  const [options, setOptions] = React.useState<ObservationCategoryOption[]>([]);

  React.useEffect(() => {
    const apiService = new ApiService();
    let cancelled = false;

    apiService.api.animals
      .animalsGetObservationOptions()
      .then((result) => {
        if (!cancelled) {
          setOptions(result.data.categories ?? []);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const selected = React.useMemo(
    () => [
      ...value.categories.map((category) => `${CATEGORY_PREFIX}${category}`),
      ...value.geneticFailures.map((failure) => `${FAILURE_PREFIX}${failure}`)
    ],
    [value]
  );

  // The backend's labels, keyed by the namespaced value the Select carries.
  const labels = React.useMemo(() => {
    const map = new Map<string, string>();

    for (const option of options) {
      if (option.description) {
        map.set(`${CATEGORY_PREFIX}${option.category}`, option.description);
      }

      for (const failure of geneticFailureOptionsFrom(option.geneticFailures)) {
        if (failure.description) {
          map.set(`${FAILURE_PREFIX}${failure.geneticFailure}`, failure.description);
        }
      }
    }

    return map;
  }, [options]);

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const picked = event.target.value as string[];

    onChange({
      categories: picked.filter((it) => it.startsWith(CATEGORY_PREFIX)).map((it) => it.slice(CATEGORY_PREFIX.length)),
      geneticFailures: picked.filter((it) => it.startsWith(FAILURE_PREFIX)).map((it) => it.slice(FAILURE_PREFIX.length))
    });
  };

  // A restored saved search draws its chips before the options have loaded, so the client's own
  // labels stay the fallback rather than being replaced by the backend's.
  const labelFor = (option: string): string =>
    labels.get(option) ??
    (option.startsWith(CATEGORY_PREFIX)
      ? observationCategoryLabel(t, option.slice(CATEGORY_PREFIX.length))
      : geneticFailureLabel(t, option.slice(FAILURE_PREFIX.length)));

  return (
    <FormControl disabled={disabled} className={className}>
      <Select
        multiple
        displayEmpty
        value={selected}
        onChange={handleChange}
        renderValue={(picked) => {
          const values = picked as string[];
          return values.length === 0 ? t('f-select-animal-observation-placeholder') : values.map(labelFor).join(', ');
        }}
        MenuProps={{
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
          getContentAnchorEl: null
        }}
      >
        {options.flatMap((option) => {
          const categoryValue = `${CATEGORY_PREFIX}${option.category}`;

          const rows = [
            <MenuItem key={categoryValue} value={categoryValue}>
              <Checkbox color="primary" checked={selected.includes(categoryValue)} />
              <ListItemText primary={labelFor(categoryValue)} />
            </MenuItem>
          ];

          // Only the genetic failure category carries defects; the others come back empty.
          for (const failure of geneticFailureOptionsFrom(option.geneticFailures)) {
            const failureValue = `${FAILURE_PREFIX}${failure.geneticFailure}`;

            rows.push(
              <MenuItem key={failureValue} value={failureValue} className="!pl-8">
                <Checkbox color="primary" checked={selected.includes(failureValue)} />
                <ListItemText primary={labelFor(failureValue)} />
              </MenuItem>
            );
          }

          return rows;
        })}
      </Select>
    </FormControl>
  );
};

export default AnimalObservationFilter;
